<!-- eslint-disable vue/prefer-true-attribute-shorthand -->
<template>
  <ais-instant-search
    :index-name="algolia.indexProducts"
    :routing="algoliaRouting"
    :search-client="algolia.searchClient"
    :insights="true"
    :class-names="{
      'ais-InstantSearch': `header-search__wrapper ${view}`,
    }"
  >
    <ais-configure :rule-contexts.camel="['search-modal']" />

    <search-form
      ref="searchForm"
      :view="view"
      @update:query="input = $event"
      @focussed="searchFocussed = true"
    />

    <overlay
      key="search-modal"
      block-class="search-modal-overlay"
      direction="down"
      :namespace="namespace"
      :show-close="false"
      type="shelf"
    >
      <template #body="overlay">
        <search-modal
          :overlay="overlay"
          :focus-elements="searchFormRefs"
          :query="input"
          :view="view"
          @update:query="input = $event"
        />
      </template>
    </overlay>
  </ais-instant-search>
</template>

<script>

/**
 * Vue: Header search (header-search)
 * -----------------------------------------------------------------------------
 * Header predictive search, Algolia powered.
 *
 */
import { mapActions, mapGetters, mapState } from 'vuex'
import aa from "search-insights"
import algoliasearch from 'algoliasearch/lite'
import { history as historyRouter } from 'instantsearch.js/es/lib/routers'
import { singleIndex as singleIndexMapping } from 'instantsearch.js/es/lib/stateMappings'
import { AisConfigure, AisInstantSearch } from 'vue-instantsearch/vue3/es'

import Overlay from '~global/overlay/overlay'
import SearchForm from '~async/search-form/search-form'
import SearchModal from '~async/search-modal/search-modal'
import { createTranslatedSearchClient } from '~/helpers/weglot-search'


const algoliaClient = algoliasearch(
  cnvs.algolia.credentials.appId,
  cnvs.algolia.credentials.apiKey,
)
const translatedAlgoliaClient = createTranslatedSearchClient(algoliaClient)

export default {
  name: 'HeaderSearch',

  components: {
    AisInstantSearch,
    AisConfigure,
    Overlay,
    SearchModal,
    SearchForm,
  },

  props: {
    view: {
      // 'desktop-form', 'mobile-form' or 'toggle-modal'
      type: String,
      default: 'desktop-form',
    },
  },

  data() {
    console.log('[Weglot search] HeaderSearch mounted search client')

    return {
      input: '',
      searchModalActive: false,
      searchFocussed: false,
      searchFormRefs: [],
      algolia: {
        indexProducts: this.$variable('algolia.indexes.products'),
        defaultFilters: this.$variable('algolia.defaultFilters'),
        searchClient: {
          ...algoliaClient,
          // Using arrow function to access component scope.
          search: (requests) => {
            console.log('[Weglot search] HeaderSearch search called', {
              searchFocussed: this.searchFocussed,
              requests: requests.map((request) => ({
                indexName: request.indexName,
                query: request?.params?.query,
              })),
            })

            // If search is not focused to not send any algolia queries.
            if (!this.searchFocussed) {
              console.log('[Weglot search] HeaderSearch blocked because search is not focussed')

              return Promise.resolve({
                results: requests.map(() => ({
                  hits: [],
                  nbHits: 0,
                  nbPages: 0,
                  page: 0,
                  processingTimeMS: 0,
                  hitsPerPage: 0,
                  exhaustiveNbHits: false,
                  query: '',
                  params: '',
                })),
              })
            }

            return translatedAlgoliaClient.search(requests)
          },
        },
        routing: {
          router: historyRouter(),
          stateMapping: singleIndexMapping(this.$variable('algolia.indexes.products')),
        },
      },
      pageType: this.$variable('page.type'),
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      settings: (state) => state.index.settings,
    }),

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
    }),

    algoliaRouting() {
      if (this.pageType !== 'search') {
        return null
      }

      return this.algolia.routing
    },

    namespace() {
      return this.view === 'desktop-form' ? 'search-modal' : 'search-modal search-modal-mobile'
    },
  },

  watch: {

    /**
     * Watch active overlay to reset current link.
     */
    async getActiveOverlay() {
      await this.$nextTick()

      if (this.getActiveOverlay?.namespace === this.namespace) {
        this.searchModalActive = true
        return
      }

      // Clear input when overlay closes
      if (this.searchModalActive) {
        this.input = ''
      }

      this.searchModalActive = false
    },

    isMobile() {
      // Close the search modal when mobile state changes
      if (this.searchModalActive) {
        this.closeOverlay(this.namespace)
        this.searchModalActive = false
        this.input = ''
      }
    },
  },

  mounted() {
    setTimeout(() => {
      const refs = []

      if (this.view === 'mobile-form' || !this.$refs.searchForm) {
        this.searchFormRefs = []
        return
      }

      if (this.$refs.searchForm.$refs.searchRef) {
        refs.push(this.$refs.searchForm.$refs.searchRef)
      }
      if (this.$refs.searchForm.$refs.searchClear) {
        refs.push(this.$refs.searchForm.$refs.searchClear)
      }

      this.searchFormRefs = refs
    }, 100) // Small delay to ensure everything is rendered

    const customerId = this.$variable('customer.id')

    aa('init', {
      appId: this.$variable('algolia.credentials.appId'),
      apiKey: this.$variable('algolia.credentials.apiKey'),
      useCookie: true,
    })

    if (this.$variable('customer.loggedIn') && customerId) {
      aa('setUserToken', String(customerId))
    }


    if (this.$variable('customer.loggedIn') && customerId) {
      // When user logged in, set the authenticated user token to the customer ID.
      aa('setAuthenticatedUserToken', String(customerId))
    } else {
      // When user log out, you can unset the authenticated token by passing undefined.
      aa('setAuthenticatedUserToken', undefined)
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),
  },
}
</script>

<style lang="scss">
@import './header-search';
</style>
